/**
 * environmentConfig.js
 * Zentrale Konfiguration für Umgebungsvariablen und Domain-basierte Einstellungen
 * Ermöglicht die automatische Erkennung und Konfiguration basierend auf Subdomains
 */

// Basis-Domain für die Produktion. Diese sollte idealerweise konfigurierbar sein
// oder aus einer zentralen Projektkonfiguration stammen.
// Für das Beispiel "ensu" ist es 'astra-it.at'.
const PROD_BASE_DOMAIN = 'astra-it.at';

// Standard-Restaurant-Slug, wird verwendet, falls keine spezifische Konfiguration gefunden wird
// oder in der Entwicklungsumgebung.
const DEFAULT_RESTAURANT_SLUG = 'ensu';

// Build-Target aus der Umgebung lesen oder Standard setzen
const SYSTEM_BUILD_TARGET = process.env.BUILD_TARGET || 'admin';

// Initialer Config-Zustand
let config = {
  apiUrl: '',
  frontendUrl: '',
  adminUrl: '',
  restaurantSlug: DEFAULT_RESTAURANT_SLUG,
  isProduction: process.env.NODE_ENV === 'production',
  isDevelopment: process.env.NODE_ENV === 'development' || process.env.NODE_ENV === undefined,
  role: 'customer', // Standardrolle, wird später angepasst
  buildTarget: SYSTEM_BUILD_TARGET, // Das Build-Target speichern
};

// Hostname und URL-Pfad für die Domain-Erkennung
const hostname = window.location.hostname;
const pathname = window.location.pathname;

// Hole den Pfad auch aus der URL-Hash-Komponente für Hash-Routing-Unterstützung
const getEffectivePath = () => {
  // Standard-Pfad aus window.location
  let path = pathname;
  
  // Prüfe auch den Hash für Hash-basiertes Routing
  const hash = window.location.hash;
  if (hash && hash.startsWith('#/')) {
    // Wenn ein Hash-Pfad existiert, hat er Vorrang
    path = hash.substring(1); // Entferne das # am Anfang
  }
  
  return path;
};

// Erkennung: Sind wir auf einer Kiosk-Domain oder Admin-Domain?
const isKioskDomain = hostname === 'kiosk.ensu.astra-it.at' || hostname.includes('kiosk.');
const isAdminDomain = hostname === 'admin.ensu.astra-it.at' || hostname.includes('admin.');

// Aktiver Pfad für die Rollenbestimmung
const activePath = getEffectivePath();
const isAdminPath = activePath.startsWith('/admin') || activePath.startsWith('/superadmin');

// Build-Target und Domain-Erkennung kombinieren:
// 1. Domain hat höchste Priorität
// 2. BUILD_TARGET hat zweithöchste Priorität
// 3. Pfad-basierte Erkennung als Fallback
let targetType = 'customer'; // Standardwert

if (isKioskDomain) {
  targetType = 'kiosk';
} else if (isAdminDomain) {
  targetType = 'admin';
} else if (SYSTEM_BUILD_TARGET === 'admin' || SYSTEM_BUILD_TARGET === 'kiosk') {
  targetType = SYSTEM_BUILD_TARGET;
} else if (isAdminPath) {
  // Pfad-basierte Erkennung als letzte Option
  targetType = 'admin';
}

// Speichere den erkannten Zieltyp
config.targetType = targetType;

// Rolle basierend auf dem erkannten Typ setzen
if (targetType === 'admin') {
  config.role = 'restaurant_admin';
} else if (targetType === 'kiosk') {
  config.role = 'customer';
}

// Produktionsumgebung: URL-Konfiguration basierend auf Subdomains
if (config.isProduction) {
  // Restaurant-Slug aus der Domain extrahieren
  let slug = DEFAULT_RESTAURANT_SLUG; // Fallback
  const parts = hostname.split('.');

  // Versuche, den Slug aus dem Hostnamen zu extrahieren
  // Annahme: [slug].astra-it.at, admin.[slug].astra-it.at, api.[slug].astra-it.at, kiosk.[slug].astra-it.at
  if (parts.length >= 3 && parts[parts.length - 2] + '.' + parts[parts.length - 1] === PROD_BASE_DOMAIN) {
    if (parts[0] === 'admin' || parts[0] === 'api' || parts[0] === 'kiosk') {
      if (parts.length >= 3) { // z.B. admin.ensu.astra-it.at -> slug = ensu
        slug = parts[1];
      }
    } else {
      slug = parts[0]; // z.B. ensu.astra-it.at -> slug = ensu
    }
  }
  config.restaurantSlug = slug;

  // URLs für Produktion
  config.apiUrl = `https://api.${config.restaurantSlug}.${PROD_BASE_DOMAIN}/api`;  // z.B. https://api.ensu.astra-it.at/api
  config.adminUrl = `https://admin.${config.restaurantSlug}.${PROD_BASE_DOMAIN}`;  // z.B. https://admin.ensu.astra-it.at
  config.kioskUrl = `https://kiosk.${config.restaurantSlug}.${PROD_BASE_DOMAIN}`;  // z.B. https://kiosk.ensu.astra-it.at
  config.frontendUrl = `https://${config.restaurantSlug}.${PROD_BASE_DOMAIN}`;     // z.B. https://ensu.astra-it.at

} else {
  // Entwicklungseinstellungen
  config.apiUrl = '/api'; // Für die Verwendung mit dem devServer Proxy
  config.frontendUrl = 'http://localhost:8080';
  config.adminUrl = 'http://localhost:8080/admin'; // Admin-Pfad
  config.kioskUrl = 'http://localhost:8081'; // Kiosk auf separatem Port
}

// Log zur Überprüfung
console.log('AppConfig initialisiert:', {
  ...config,
  role: config.role,
  targetType: config.targetType,
  buildTarget: SYSTEM_BUILD_TARGET,
  hostname,
  activePath,
  isDevelopment: config.isDevelopment
});

// Exportiere die Konfiguration und einzelne Werte für einfachen Zugriff
export const AppConfig = Object.freeze({ ...config }); // Freeze, um unbeabsichtigte Änderungen zu verhindern

// Exportiere häufig verwendete Werte direkt für Bequemlichkeit
export const API_URL = AppConfig.apiUrl;
export const FRONTEND_URL = AppConfig.frontendUrl;
export const ADMIN_URL = AppConfig.adminUrl;
export const KIOSK_URL = AppConfig.kioskUrl;
export const RESTAURANT_SLUG = AppConfig.restaurantSlug;
export const CURRENT_ROLE = AppConfig.role;
export const TARGET_TYPE = AppConfig.targetType;
export const BUILD_TARGET = AppConfig.buildTarget;
export const IS_PRODUCTION = AppConfig.isProduction;
export const IS_DEVELOPMENT = AppConfig.isDevelopment;

// Funktion zum dynamischen Aktualisieren der Rolle nach Navigation
// Diese kann vom Router aufgerufen werden
export function updateRoleBasedOnRoute(path) {
  const isAdminRoute = path?.startsWith('/admin') || path?.startsWith('/superadmin');
  
  if (isAdminRoute && config.role !== 'restaurant_admin' && !isKioskDomain) {
    // Nur aktualisieren, wenn wir nicht auf einer spezifischen Domain sind
    config.role = 'restaurant_admin';
    console.log(`Rolle aufgrund der Route ${path} auf ${config.role} aktualisiert`);
    return true;
  } else if (!isAdminRoute && config.role !== 'customer' && !isAdminDomain) {
    config.role = 'customer';
    console.log(`Rolle aufgrund der Route ${path} auf ${config.role} aktualisiert`);
    return true;
  }
  
  return false; // Keine Änderung
}
